import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  forwardRef,
  OnInit,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MultiSelect, MultiSelectModule } from 'primeng/multiselect';
import { After } from 'v8';

@Component({
  selector: 'ppl-multiselect',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MultiSelectModule],
  templateUrl: './multiselect.component.html',
  styleUrl: './multiselect.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiselectComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => MultiselectComponent),
      multi: true,
    },
  ],
})
export class MultiselectComponent
  implements ControlValueAccessor, Validator, OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() options: any[] = [];
  @Input() placeholder: string = '';
  @Input() optionLabel: any;
  @Input() optionValue: any;
  @Input() disabled: boolean = false;
  @Input() showClear = false;
  @Input() maxTagCount = 3;
  @Input() ariaLabel: string = '';
  @ViewChild('multiSelect') multiSelect!: MultiSelect;
  private handleTabKeyBound!: (event: KeyboardEvent) => void;

  selectedItems: any[] = [];
  selectedCodes: any[] = [];
  pendingValue: any[] = [];
  ariaMessage = '';
  onChange = (_: any) => { };
  onTouched = () => { };
  constructor(private elementRef: ElementRef) { }

  ngOnInit(): void {
  }

  ngAfterViewInit() {
    const el = this.multiSelect.el.nativeElement; // root element of the component
    this.handleTabKeyBound = (event: KeyboardEvent) => this.handleTabKey(event);
    el.addEventListener(
      'keydown',
      (event: KeyboardEvent) => this.handleTabKeyBound(event),
      true // capture phase
    );

    if (this.selectedCodes.length > 0) {
      setTimeout(() => this.setTabIndexOnClearIcon(), 100);
    }
  }

  handleTabKey(event: KeyboardEvent) {
    if (event.key === 'Tab') {
      // stop PrimeNG’s default Tab behavior
      event.stopImmediatePropagation();

      // close overlay if open
      if (this.multiSelect.overlayVisible) {
        this.multiSelect.hide(true);
      }

      // Allow natural tab navigation (no preventDefault)
    }
  }

  onSelect(codes: string[] | null) {
    if (!codes || !Array.isArray(codes)) {
      this.selectedCodes = [];
      this.selectedItems = [];
      this.onChange([]);
      return;
    }
    this.selectedCodes = codes;
    this.selectedItems = codes
      .map((code) =>
        this.options?.find((opt) => opt[this.optionValue] === code)
      )
      .filter(Boolean);

    this.onChange(this.selectedCodes);
    this.announceSelectedValue();
  }

  setTabIndexOnClearIcon() {
    const clearIcon = this.elementRef.nativeElement.querySelector(
      '.p-multiselect-clear-icon'
    );

    if (clearIcon) {
      clearIcon.setAttribute('tabindex', '0');
      clearIcon.removeAttribute('aria-hidden');
      clearIcon.setAttribute('role', 'button');
      clearIcon.setAttribute('aria-label', 'Clear all selected items');
    }
  }

  remove(item: any) {
    const valueToRemove =
      typeof item === 'object' ? item[this.optionValue] : item;

    this.selectedCodes = this.selectedCodes.filter(
      (code) => code !== valueToRemove
    );
    this.mapSelectedItems();
    this.onChange(this.selectedCodes);
    this.announceSelectedValue();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['options'] &&
      this.options?.length > 0 &&
      this.pendingValue?.length
    ) {
      this.selectedCodes = this.pendingValue;
      this.mapSelectedItems();
      this.pendingValue = [];
    }
  }

  mapSelectedItems() {
    this.selectedItems = this.selectedCodes
      .map((code) => {
        if (typeof code === 'object' && code !== null) {
          return code;
        }
        return this.options.find((opt) => opt[this.optionValue] === code);
      })
      .filter(Boolean);
  }

  writeValue(obj: any): void {
    if (!obj || !Array.isArray(obj)) {
      this.selectedCodes = [];
      this.selectedItems = [];
      return;
    }
    this.selectedCodes = obj;

    if (this.selectedCodes.length > 0) {
      this.setTabIndexOnClearIcon();
    }

    if (!this.options?.length) {
      this.pendingValue = obj;
      return;
    }
    this.mapSelectedItems();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  isSelected(item: any): boolean {
    const value = typeof item === 'object' ? item[this.optionValue] : item;
    return this.selectedCodes?.includes(value);
  }

  getLabel(item: any): string {
    if (item && typeof item === 'object' && item[this.optionLabel]) {
      return item[this.optionLabel];
    }
    const match = this.options.find((opt) => opt[this.optionValue] === item);
    return match ? match[this.optionLabel] : item;
  }

  validate() {
    return null;
  }

  announceSelectedValue() {
    if (this.selectedItems.length > 0) {
      let selectedLabels = this.selectedItems.map(item => this.getLabel(item)).join(', ');
      this.ariaMessage = `${this.placeholder}: ${selectedLabels} selected.`;
    } else {
      this.ariaMessage = `${this.placeholder}. No item selected.`;
    }
  }

  ngOnDestroy() {
    const el = this.multiSelect?.el?.nativeElement;
    if (el && this.handleTabKeyBound) {
      el.removeEventListener('keydown', this.handleTabKeyBound, true);
    }
  }
}
